export const PAGE_TYPES = [
  { value: 'home', label: 'Home' },
  { value: 'tournament_list', label: 'Tournaments List' },
  { value: 'tournament_detail', label: 'Tournament Detail' },
  { value: 'player_list', label: 'Players List' },
  { value: 'player_detail', label: 'Player Detail' },
  { value: 'news_list', label: 'News List' },
  { value: 'news_article', label: 'News Article' },
  { value: 'search', label: 'Search' },
] as const

export type PageTypeValue = typeof PAGE_TYPES[number]['value']

export const getPageTypeLabel = (pageType: string | null): string => {
  if (!pageType) return 'All Pages'
  const found = PAGE_TYPES.find(pt => pt.value === pageType)
  return found ? found.label : pageType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Page types whose pages are dynamic (one per tournament/player), addressable
// by URL slug. A placement under these can optionally be scoped to a single
// tournament/player via targeting_rules.slug. Keep in sync with SLUG_PAGE_TYPES
// in apps/api/routes/merchandising/models.py.
export const SLUG_PAGE_TYPES = ['tournament_detail', 'player_detail'] as const

// Per-slug-page-type config driving the CMS picker: which entity to search and
// which slug entity_type to resolve the canonical URL slug from (/slugs API).
export const getSlugTargetConfig = (pageType: string | undefined | null):
  | { kind: 'competition'; slugEntityType: 'competition_parent'; noun: string }
  | { kind: 'competitor'; slugEntityType: 'player'; noun: string }
  | null => {
  if (pageType === 'tournament_detail') {
    return { kind: 'competition', slugEntityType: 'competition_parent', noun: 'tournament' }
  }
  if (pageType === 'player_detail') {
    return { kind: 'competitor', slugEntityType: 'player', noun: 'player' }
  }
  return null
}

// Iframe URL a page-type preview loads — a real, representative page of that
// type, so a detail-page preview actually shows a detail page. For *_detail
// types this is a concrete sample entity; its merch context (page type + slug)
// is derived from the URL on the tennis.com side, no override headers needed.
//
// The sample slugs must be CANONICAL: the detail routes permanent-redirect a
// non-canonical slug to its canonical form, and a browser-followed redirect
// drops the preview query string, turning the iframe into a plain live render.
// Use evergreen entities (the competition_parent for tournaments, a long-
// tenured player, a notable article) so they don't 404 as data ages.
const PAGE_TYPE_PREVIEW_PATHS: Record<string, string> = {
  home: '/',
  tournament_list: '/tournaments',
  tournament_detail: '/tournaments/wimbledon',
  player_list: '/players-rankings',
  player_detail: '/players-rankings/carlos-alcaraz',
  news_list: '/news',
  news_article:
    '/news/articles/alexander-zverev-battles-past-flavio-cobolli-for-first-grand-slam-title-of-career-at-roland-garros',
  // Tennis Channel only. Its merchandised region sits below the search UI, so
  // the preview opens the page with no query, exactly as a viewer arrives.
  search: '/search',
}

export const getPreviewPathForPageType = (pageType: string | undefined): string => {
  if (!pageType) return '/'
  return PAGE_TYPE_PREVIEW_PATHS[pageType] ?? '/'
}
